// src/data/roleScreens.js
//
// RULING 170 (2026-09-17, from Bob's 17 Sept decision D-20): each role sees only the screens its role
// mandates - in the handbooks and in the platform. This file is the ONE list of which page ids a role
// may open. navForRole() in App.js declares the sidebar items (NAV-1 reads them there) and filters
// them through mayOpen(); renderPage() refuses any page id mayOpen() denies, so a page cannot be
// reached by a stray setPage() that the sidebar never offered. One source, two readers (directives XI).
//
// RULING 126 (2026-09-14) READ RULING 105 AGAINST THE RECORD AND SETTLED IT: "A MEMBER IS A CLAIMANT ON
// HIS OWN CLAIMS"; 105's "cannot file, edit or forward" binds every claim that is NOT his; and 126 was
// built as "no page gate removes New Claim from a member". This file removed them anyway on 17 Sept,
// citing 105 alone - so from then until 2026-09-20 a member could not file at all, against the founding
// path and a dozen rulings (29, 133-144, 153, 155; RLA 45 U.S.C. 153 First (i)/(j); SMART Constitution
// Sec. 44). The filing doors are his again. He still gets NO New Case: opening a discipline case is the
// officer's act (ruling 105 on the discipline path, unchanged).
//
// 'tester' is the platform's own test account role (App.js roleDisplayMap). It keeps the filing doors
// because test claims are what it exists to file. It gets no desk and no committee.
//
// Detail pages (claim-detail, case-detail, full-form) are not sidebar items; they are reached from a
// list the role can already open, and are listed here so the gate knows them.

// SECTION 533 (2026-09-30): the reports are an officer's screen - Local and General Chairpersons.
// RULING 264 (2026-09-30): every role has the union calendar - what each sees is the rules' and unionCalendar.js's.
const F = Object.freeze;
export const SCREENS = F({
  member:        F(['dashboard', 'claims', 'claim-detail', 'new-claim', 'full-form', 'quick-file',
                  'discipline', 'case-detail', 'agreements', 'boards', 'messages', 'calendar', 'member-rights', 'onboarding']),   // ruling 306: every role
  tester:        F(['dashboard', 'claims', 'claim-detail', 'new-claim', 'full-form', 'quick-file',
                  'discipline', 'new-case', 'case-detail', 'agreements', 'boards', 'messages', 'calendar', 'member-rights', 'onboarding']),   // ruling 306: every role
  local_chair:   F(['dashboard', 'claims', 'claim-detail', 'new-claim', 'full-form', 'quick-file',
                  'discipline', 'new-case', 'case-detail', 'agreements', 'desk', 'applications', 'member-files', 'boards', 'messages', 'calendar', 'reports', 'member-rights', 'onboarding']),
  general_chair: F(['dashboard', 'claims', 'claim-detail', 'new-claim', 'full-form', 'quick-file',
                  'discipline', 'new-case', 'case-detail', 'agreements', 'desk', 'applications', 'member-files',
                  'committee', 'readiness', 'committee-board', 'war-room', 'boards', 'messages', 'calendar', 'reports', 'member-rights', 'onboarding']),
  // RECORD 675 (Bob, 2026-10-13): the onboarding-only General Chairperson account (ruling 288 as amended 2026-10-09)
  // sees the Onboarding page and nothing else until his committee is complete. 'onboarding' is on every list: one page,
  // every role's part (a footer link for the four roles above, the only sidebar item for this one).
  gc_onboarding: F(['onboarding', 'member-rights']),
});

// The administrator reaches every screen the platform has. Ruling 169 (D-19) keeps that during the
// build, under a banner the War Room shows him and nobody else.
export function roleKey(user) {
  if (!user) return null;
  if (user.isAdmin || user.role === 'admin') return 'admin';
  return SCREENS[user.role] ? user.role : 'member';   // an unknown role gets the narrowest set, not the widest
}

export function screensForRole(user) {
  const k = roleKey(user);
  if (!k) return [];
  if (k === 'admin') return Array.from(new Set(Object.values(SCREENS).flat()));
  return SCREENS[k].slice();
}

export function mayOpen(user, pageId) {
  if (!user || typeof pageId !== 'string') return false;
  // RULING 320 (2026-10-16): the Local's own offices (Secretary & Treasurer, President ...) are held by designation, not by
  // role - an S&T is often a member by role. His work area opens to the office, read from the token (localOffices).
  if (pageId === 'local-office') return roleKey(user) === 'admin' || (Array.isArray(user.localOffices) && user.localOffices.length > 0);
  return screensForRole(user).includes(pageId);
}

// What the gate shows instead of the page: plain words, for everyone. The ruling number is NOT in
// this sentence - src\components\RulingRef.js puts it behind "Why?" for a member and as "[R-170]"
// for an officer (ruling 168), and App.js draws that beside this text.
export function refusalText(user) {
  const officer = user && (user.role === 'local_chair' || user.role === 'general_chair');
  return officer
    ? 'This screen is not part of your office’s work on the platform.'
    : 'This screen is not part of your role on the platform.';
}
